import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import Button from "./Buttons";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import UserSelect from "./UserSelect";

const RiskAcceptanceModal = ({ show, onClose, risk, onAccepted, acceptEndpoint, fieldPrefix, riskId, riskType }) => {
    const apiCall = useApi();
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [reason, setReason] = useState("");
    const [targetDate, setTargetDate] = useState("");
    const [expiryDate, setExpiryDate] = useState("");
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Fetch users when modal opens
    useEffect(() => {
        if (show) fetchUsers();
    }, [show]);

    const fetchUsers = async () => {
        try {
            const res = await apiCall("GET", "/users");
            setUsers(res || []);
        } catch (err) {} finally {
            setLoadingUsers(false);
        }
    };

    const handleAccept = async () => {
        if (!selectedUserId || !reason.trim() || isProcessing) return;
        setIsProcessing(true);

        const p = fieldPrefix || '';
        const payload = {
            userReason: reason.trim(),
            riskAcceptedByUserId: user?.id,
            primaryApproverUserId: parseInt(selectedUserId),
            remediationTargetDate: targetDate ? new Date(targetDate).toISOString() : null,
            acceptanceExpiryDate: expiryDate ? new Date(expiryDate).toISOString() : null,
        };
        if (p) {
            payload[`${p}UserReason`] = reason.trim();
        }

        try {
            const endpoint = acceptEndpoint || `/risks/${risk.riskId}/accept`;
            await apiCall("PUT", endpoint, payload);

            // Write activity log
            if (riskId && riskType) {
                try {
                    await apiCall("POST", `/allRemediations/logs/by-risk`, {
                        remediationType: riskType,
                        riskId,
                        previousStatus: null,
                        newStatus: "AcceptancePendingPrimaryApproval",
                        comment: reason.trim(),
                        changedByUserName: user?.userName || user?.email || "",
                        changeType: "ApprovalPending",
                        targetDate: targetDate ? new Date(targetDate).toISOString() : null,
                    });
                } catch (logErr) {}
            }

            setSuccessMessage('Risk acceptance submitted for approval. A notification email has been sent to the approver. Please check your spam/junk folder if you do not receive it in your inbox.');
            onAccepted();
            setTimeout(() => { setSuccessMessage(''); onClose(); }, 10000);
        } catch (err) {
            alert("Failed to accept risk.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Modal isOpen={show} onClose={onClose} title="Accept Risk">
            <div className="space-y-5">
                {/* Approver */}
                <div>
                    <label className="block text-sm font-medium text-content-secondary mb-2">
                        Approver (Authorized User)<span className="text-red-500 ml-1">*</span>
                    </label>
                    <UserSelect
                        value={selectedUserId}
                        onChange={setSelectedUserId}
                        users={users}
                        loading={loadingUsers}
                        onInviteSuccess={fetchUsers}
                    />
                </div>

                {/* Reason */}
                <div>
                    <label className="block text-sm font-medium text-content-secondary mb-2">
                        Reason for Accepting Risk<span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                        rows="3"
                        placeholder="Explain why this risk is being accepted..."
                        required
                    />
                </div>

                {/* Remediation Target Date */}
                <div>
                    <label className="block text-sm font-medium text-content-secondary mb-2">
                        Remediation Target Date
                    </label>
                    <input
                        type="date"
                        value={targetDate}
                        onChange={(e) => setTargetDate(e.target.value)}
                        className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                    />
                </div>

                {/* Acceptance Expiry Date */}
                <div>
                    <label className="block text-sm font-medium text-content-secondary mb-2">
                        Acceptance Expiry Date
                    </label>
                    <input
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                    />
                    <p className="mt-1 text-xs text-content-secondary">
                        The acceptance lapses on this date and the risk must be re-reviewed. Leave blank for no expiry.
                    </p>
                </div>

                {/* Success message */}
                {successMessage && (
                    <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                        {successMessage}
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleAccept}
                        disabled={!reason.trim() || !selectedUserId || isProcessing || !!successMessage}
                    >
                        {isProcessing ? 'Submitting...' : 'Accept Risk'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RiskAcceptanceModal;
